import { PLAN_LIMITS, SELECTABLE_PLAN_YEARS } from './constants.js'
import { getEmployeesForMetrics } from './calculations.js'

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const money = (value) => Math.max(0, finite(value))
const sumBy = (rows, accessor) => rows.reduce((total, row) => total + money(accessor(row)), 0)

export const isExplicitOwner = (employee) => (
  employee?.isFivePercentOwner === true || finite(employee?.ownershipPercent) > 5
)

const limitsForYear = (planYear) => (
  PLAN_LIMITS[Number(planYear)] || PLAN_LIMITS[SELECTABLE_PLAN_YEARS[0]]
)

// This chart deliberately uses each calculated employee's own §415(c) limit.
// That value is already the lesser of the indexed dollar limit and applicable
// compensation. Catch-up contributions and cash-balance benefit credits remain
// outside the bar because neither belongs in DC annual additions under §415(c).
export function buildOwnerSavingsCapacityData(calculatedEmployees = [], planYear) {
  const limits = limitsForYear(planYear)
  return calculatedEmployees
    .filter(isExplicitOwner)
    .map((employee) => {
      const regularDeferral = money(employee.baseDeferral)
      const afterTax = money(employee.afterTaxContribution)
      const employer = money(employee.employerTotal) + money(employee.qnecAmount)
      const forfeitures = money(employee.forfeitureAllocation)
      const componentTotal = regularDeferral + afterTax + employer + forfeitures
      const annualAdditions = money(employee.totalAdditions || componentTotal)
      const compensation = money(employee.compensation)
      // A calculated $0 limit is meaningful for an owner with no eligible
      // compensation. Only fall back when the engine value is actually absent
      // or non-numeric; `||` would incorrectly turn that valid zero into the
      // full indexed dollar limit.
      const hasCalculatedLimit = employee.limit415c !== null
        && employee.limit415c !== undefined
        && employee.limit415c !== ''
        && Number.isFinite(Number(employee.limit415c))
      const limit415c = hasCalculatedLimit
        ? money(employee.limit415c)
        : Math.min(limits.planLimit415c, compensation)
      const catchupLimit = money(employee.catchupLimit)
      const statutoryDeferralCeiling = Math.min(
        compensation,
        limits.deferralLimit + catchupLimit,
      )
      return {
        id: String(employee.id || ''),
        name: String(employee.name || employee.id || 'Owner'),
        employeeIds: employee.id ? [employee.id] : [],
        regularDeferral,
        afterTax,
        employer,
        forfeitures,
        otherAdditions: Math.max(0, annualAdditions - componentTotal),
        annualAdditions,
        limit415c,
        remaining415c: Math.max(0, limit415c - annualAdditions),
        employeeDeferral: money(employee.totalEmployeeContribution),
        statutoryDeferralCeiling,
        remainingEmployeeDeferral: Math.max(
          0,
          statutoryDeferralCeiling - money(employee.totalEmployeeContribution),
        ),
        catchupContribution: money(employee.catchupContribution),
        catchupLimit,
        cashBalanceCredit: money(employee.cashBalanceCredit),
        limitApplied: employee.exceeds415c === true,
      }
    })
    .sort((a, b) => b.remaining415c - a.remaining415c || a.name.localeCompare(b.name))
}

// Each point is an actually calculated plan slot. Cash-balance amounts are
// called modeled allocations—not costs—because a benefit credit is not the
// plan's required contribution. The UI repeats that actuarial funding is needed.
export function buildOwnerPlanComparisonData(entries = []) {
  return entries.flatMap((entry) => {
    if (!entry?.plan || !entry?.analysis?.calculatedEmployees?.length) return []
    const employees = getEmployeesForMetrics(
      entry.analysis.calculatedEmployees,
      entry.plan.terminationHandling || {},
    )
    const owners = employees.filter(isExplicitOwner)
    if (!owners.length) return []
    const nonOwners = employees.filter((employee) => !isExplicitOwner(employee))
    const ownerEmployeeSavings = sumBy(
      owners,
      (employee) => money(employee.totalEmployeeContribution) + money(employee.afterTaxContribution),
    )
    const ownerDcEmployer = sumBy(owners, (employee) => employee.dcEmployerCost)
    const ownerCashBalance = sumBy(owners, (employee) => employee.cashBalanceCredit)
    const nonOwnerDcEmployer = sumBy(nonOwners, (employee) => employee.dcEmployerCost)
    const nonOwnerCashBalance = sumBy(nonOwners, (employee) => employee.cashBalanceCredit)
    return [{
      key: String(entry.key || ''),
      name: String(entry.plan.name || 'Plan'),
      ownerCount: owners.length,
      nonOwnerCount: nonOwners.length,
      ownerEmployeeSavings,
      ownerDcEmployer,
      ownerCashBalance,
      ownerAllocation: ownerEmployeeSavings + ownerDcEmployer + ownerCashBalance,
      nonOwnerDcEmployer,
      nonOwnerCashBalance,
      nonOwnerAllocation: nonOwnerDcEmployer + nonOwnerCashBalance,
      hasCashBalance: ownerCashBalance > 0 || nonOwnerCashBalance > 0,
      limitApplied: employees.some((employee) => employee.exceeds415c === true),
    }]
  })
}
